import {
  FormTemplateAttribute,
  FormTemplateFieldTypes,
  generateFormSlug,
  getFormFieldDefaultSubtext,
} from 'mapistry-shared';
import { v4 as uuidv4 } from 'uuid';
import {
  EditableFormTemplate,
  EditableFormTemplateField,
  EditableFormTemplateFieldGroup,
} from '../FormTemplateEditor/types';

function defaultProps(
  fieldType: FormTemplateFieldTypes,
): Pick<
  EditableFormTemplateField,
  'id' | 'isRequired' | 'nameSubtext' | 'type' | 'helpText'
> {
  return {
    helpText: '',
    id: uuidv4(),
    isRequired: true,
    nameSubtext: getFormFieldDefaultSubtext(fieldType) ?? null,
    type: fieldType,
  };
}

const makeCoreQuestions = (): EditableFormTemplateField[] => [
  {
    ...defaultProps(FormTemplateFieldTypes.BOOLEAN),
    name: '[Question 1]',
    defaultOption: 'Unselected',
  },
  {
    ...defaultProps(FormTemplateFieldTypes.BOOLEAN),
    name: '[Question 2]',
    defaultOption: 'Unselected',
  },
  {
    ...defaultProps(FormTemplateFieldTypes.SINGLE_SELECT),
    name: '[Question 3]',
    options: ['Yes', 'No', 'N/A'],
  },
];

const makePhotoAndTaskField = (): EditableFormTemplateField[] => [
  {
    ...defaultProps(FormTemplateFieldTypes.PHOTO_CAROUSEL),
    name: 'Photo(s)',
    isRequired: false,
  },
  {
    ...defaultProps(FormTemplateFieldTypes.TASKS),
    name: 'Task(s)',
    isRequired: false,
  },
];

const INSPECTION_INFO_SECTION_FIELDS: EditableFormTemplateField[] = [
  {
    ...defaultProps(FormTemplateFieldTypes.EXPLANATION_AREA),
    name: '[Instructions]',
    helpText: '[insert instructions]',
    isRequired: false,
  },
  {
    ...defaultProps(FormTemplateFieldTypes.DATE),
    name: 'Inspection Date',
    attributes: [FormTemplateAttribute.IS_INSPECTION_DATE],
    dynamicallyGenerateDefault: true,
  },
  {
    ...defaultProps(FormTemplateFieldTypes.USER),
    dynamicallyGenerateDefault: true,
    name: 'Inspector Name',
  },
  {
    ...defaultProps(FormTemplateFieldTypes.SIGNATURE),
    name: "Inspector's Signature",
  },
];

const DUPLICABLE_SECTION_FIELDS: EditableFormTemplateField[] = [
  {
    ...defaultProps(FormTemplateFieldTypes.TEXT_AREA),
    name: '[Inspection Area/Item - (text area)]',
    isDuplicableDisplayField: true,
  },
  ...makeCoreQuestions(),
  {
    ...defaultProps(FormTemplateFieldTypes.TEXT_AREA),
    name: 'Notes',
    isRequired: false,
  },
  ...makePhotoAndTaskField(),
];

const NON_DUPLICABLE_SECTION_FIELDS: EditableFormTemplateField[] = [
  ...makeCoreQuestions(),
  {
    ...defaultProps(FormTemplateFieldTypes.TEXT_AREA),
    name: 'Comments',
    isRequired: false,
  },
  ...makePhotoAndTaskField(),
];

const COMMENT_SECTION_FIELDS: EditableFormTemplateField[] = [
  {
    ...defaultProps(FormTemplateFieldTypes.TEXT_AREA),
    name: 'Comments',
    isRequired: false,
  },
];

const DEFAULT_GROUPS: EditableFormTemplateFieldGroup[] = [
  {
    name: '[Inspection Info]',
    isDuplicable: false,
    isStencilProtected: false,
    slug: generateFormSlug(),
    fields: INSPECTION_INFO_SECTION_FIELDS,
  },
  {
    name: '[Inspection Areas (repeatable)]',
    isDuplicable: true,
    isStencilProtected: false,
    slug: generateFormSlug(),
    fields: DUPLICABLE_SECTION_FIELDS,
  },
  {
    name: '[Inspection Questions]',
    isDuplicable: false,
    isStencilProtected: false,
    slug: generateFormSlug(),
    fields: NON_DUPLICABLE_SECTION_FIELDS,
  },
  {
    name: '[Comments]',
    isDuplicable: false,
    isStencilProtected: false,
    slug: generateFormSlug(),
    fields: COMMENT_SECTION_FIELDS,
  },
];

export const DEFAULT_STARTER_FORM_TEMPLATE: EditableFormTemplate = {
  id: uuidv4(),
  name: '',
  slug: '', // gets overwritten by random slug with org name, or manually by admin
  attributes: [],
  category: null,
  groups: DEFAULT_GROUPS,
};
